/**
 * What every OCEAN view can do, provided by OceanApp: read and change saved
 * state, share, and move through the PRD's navigation model —
 *   problem clicked anywhere  -> it replaces the base card (drawer closes)
 *   company / solution clicked from the base   -> the drawer opens with it
 *   company / solution clicked inside a drawer -> pushed onto the drawer (Back pops)
 *   a COSMOS technology clicked anywhere     -> the Cosmos technology card (App's modal)
 */
import { createContext, useContext } from 'react';
import type { Kind, SavedRef, Scope } from './api';

export interface CardRef {
  type: Kind;
  id: string;
  /** What the breadcrumb calls it (a company's or technology's name). */
  label?: string;
  /** Where the card was opened from: a company card's relevance score; a
   *  problem card's related problems also follow the search words (q). */
  ctx?: { problem?: string; q?: string };
}

export interface OceanCtx {
  savedOf: (kind: Kind, id: string) => SavedRef | null;
  toggleSave: (kind: Kind, id: string) => void;
  /** Opens the collection picker (save, move, remove); from: where the bookmark was. */
  chooseCollection: (kind: Kind, id: string, from?: 'card' | 'list') => void;
  /** Open a COSMOS technology: the Cosmos card, exactly as Cosmos opens it
   *  (relevance: the engine's score, for a scored match). */
  openTech: (id: string, relevance?: number, label?: string, from?: 'base' | 'drawer') => void;
  toast: (msg: string) => void;
  openProblem: (id: string, from: string, ctx?: CardRef['ctx']) => void;
  openCompanyBase: (id: string, ctx: CardRef['ctx'] | undefined, from: string) => void;
  /** From the base card: the drawer starts over with this card. */
  openFromBase: (ref: CardRef) => void;
  /** From inside the drawer: this card goes on top (Back returns). */
  pushDrawer: (ref: CardRef) => void;
  /** The month every source is known to have existed by ("June 2026"). */
  floorLabel: string;
  isMobile: boolean;
  /** The cards' similar / related / COSMOS lists: within the card's industry or all
   *  (null until someone switches: the server's default applies). */
  scope: Scope | null;
  setScope: (s: Scope) => void;
}

export const OceanContext = createContext<OceanCtx | null>(null);

export function useOcean(): OceanCtx {
  const v = useContext(OceanContext);
  if (!v) throw new Error('useOcean outside OceanApp');
  return v;
}
